import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClaimCandidateGroupMember } from "@hp/types";
import { Button, Card, CardBody, CardHeader, CardTitle } from "@hp/ui";
import { validateClaimTranches } from "../data/index";
import { formatINR } from "../lib/workbenchFormat";
import { ClaimStatusPill } from "./ClaimStatusPill";
import { needsCounselValidation } from "../lib/claimStatusDisplay";

interface ClaimValidatePanelProps {
  claimId: string;
  members: ClaimCandidateGroupMember[];
}

export function ClaimValidatePanel({ claimId, members }: ClaimValidatePanelProps) {
  const queryClient = useQueryClient();
  const pending = members.filter(
    (member) => member.status && needsCounselValidation(member.status, member.counsel_validated_at),
  );
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setSelected(new Set());
  }, [claimId, members.length]);

  const mutation = useMutation({
    mutationFn: (claimIds: string[]) => validateClaimTranches(claimIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claim-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["claim-candidates", claimId] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setSelected(new Set());
    },
  });

  if (pending.length === 0) {
    return null;
  }

  const allSelected = pending.every((member) => selected.has(member.id));
  const someSelected = pending.some((member) => selected.has(member.id));

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(pending.map((member) => member.id)));
  }

  function toggleOne(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Validate tranches</CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          This claim is in progress. Select one or more financial years, or validate all at once, before marking
          tranches as counsel-validated.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleAll}
                      aria-label="Select all pending tranches"
                    />
                    <span>Select</span>
                  </label>
                </th>
                <th className="px-4 py-2.5 font-medium">FY</th>
                <th className="px-4 py-2.5 font-medium">Finding</th>
                <th className="px-4 py-2.5 font-medium">Total</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pending.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={selected.has(member.id)}
                      onChange={() => toggleOne(member.id)}
                      aria-label={`Select ${member.financial_year ?? "tranche"}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-slate-700">
                    {member.financial_year ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-slate-600">
                    {member.finding_case_number ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums text-slate-900">
                    {member.value_high != null ? formatINR(member.value_high) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {member.status ? (
                      <ClaimStatusPill status={member.status} counselValidatedAt={member.counsel_validated_at} />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            disabled={selected.size === 0 || mutation.isPending}
            onClick={() => mutation.mutate([...selected])}
          >
            Validate selected ({selected.size})
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(pending.map((member) => member.id))}
          >
            Validate all {pending.length}
          </Button>
          {mutation.isError ? (
            <span className="text-xs text-red-600">
              {mutation.error instanceof Error ? mutation.error.message : "Validation failed"}
            </span>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
